/**
 * Generic DocType List Page Shell
 *
 * Reusable page component for any doctype list view with server-side filtering,
 * pagination, sorting, and search. Provides consistent behavior across all doctypes.
 *
 * This is the orchestrator — hooks/state live here, rendering is delegated to:
 * - ListToolbar (compact + standard toolbar)
 * - ListTableBody (table header + body + empty state)
 * - ListFilterModals (filter modals + column sidebar)
 * - useShellColumns (column building + useReactTable)
 * - DensityDropdown (density/text size selector — used inside ListToolbar)
 */

import { useMemo, useState, useEffect, useCallback } from "react";
import type { PaginationState, SortingState, ExpandedState, RowSelectionState } from "@tanstack/react-table";
import { asExpandedRecord } from "@/lib/utils/tableExpansion";
import { cn } from "@/lib/utils";
import { DataTablePagination } from "@/components/data-table/data-table-pagination";
import { PageContainer } from "@/components/layout/PageContainer";
import { useDebounce } from "@/lib/hooks/useDebounce";
import { useFilterStore } from "@/lib/store/filterStore";
import { usePreferencesStore } from "@/lib/store/preferencesStore";
import type { FilterDescriptor } from "@/lib/erp";
import type { ListViewLayoutMode } from "@/lib/views/types";
import { useURLFilters } from "@/lib/hooks/useURLFilters";
import { useSearchParams } from "react-router-dom";
import { useMetaDrivenListSettings } from "@/lib/hooks/useMetaDrivenListSettings";
import type { ListSettingsFieldConfig } from "@/lib/hooks/listSettingsTypes";
import { useUserAvatars } from "../api/useUserAvatars";
import { parseLikedBy } from "../utils/parseLikedBy";
import { GalleryGridLayout } from "./GalleryGridLayout";
import { RevoGridReport } from "./report";
import { useReportData } from "../api/useReportData";
import { AdvancedTableView } from "@/components/advanced-table/AdvancedTableView";
import { useAdvancedTableState } from "@/components/advanced-table/useAdvancedTableState";

// Extracted modules
import type { DocTypeListPageShellProps, NormalizedSystemConfig } from "./DocTypeListPageShell.types";
import { EMPTY_FILTERS, EMPTY_SORTING } from "./DocTypeListPageShell.types";
import { useShellColumns } from "./useShellColumns";
import { ListToolbar } from "./ListToolbar";
import { ListTableBody } from "./ListTableBody";
import { ListFilterModals } from "./ListFilterModals";
import { useFieldEditability } from "@/features/common/hooks/useFieldEditability";
import { useInlineTableEditing } from "@/components/inline-edit/useInlineTableEditing";
import { SelectionFloatingBar } from "@/components/inline-edit/SelectionFloatingBar";
import { useShellBulkActions } from "./useShellBulkActions";
import { useShellReportConfig } from "./useShellReportConfig";
import { useShellGalleryFields } from "./useShellGalleryFields";
import { usePublishListContext } from "@/features/ai-context/usePublishListContext";
import { useListScreenSection } from "@/features/ai-context/useListScreenSection";

// ---------- Main component ----------

export function DocTypeListPageShell<T>(props: DocTypeListPageShellProps<T>) {
  const {
    doctype,
    columns,
    filterStoreKey,
    useListHook,
    defaultSorting = EMPTY_SORTING,
    getSubRows,
    defaultExpanded,
    searchPlaceholder = "Search...",
    title,
    description,
    newButtonLabel,
    onNewClick,
    onRowClick,
    getRowId = (row: T) => {
      const rowWithName = row as Record<string, unknown> & { name?: string };
      return (rowWithName?.name as string | undefined) ?? String(row);
    },
    translations = {},
    header,
    headerBelow,
    className,
    renderSubRow,
    activeSubRowId,
  } = props;

  // Parse URL filters and apply to filter store (enables shareable filtered links)
  useURLFilters(filterStoreKey);

  // View mode state with URL + localStorage persistence
  const [searchParams, setSearchParams] = useSearchParams();
  const urlViewMode = searchParams.get('view') as ListViewLayoutMode | null;

  const storedViewMode = usePreferencesStore(
    useCallback((s) => s.docTypeViewMode[doctype], [doctype])
  );

  // Build set of allowed view modes based on enabled props
  const allowedViewModes = useMemo(() => {
    const modes = new Set<ListViewLayoutMode>(['table']);
    if (props.enableViewModeSwitch) modes.add('gallery');
    if (props.enableSimpleView) modes.add('simple');
    if (props.enableReportView) modes.add('report');
    if (props.enableAdvancedView) modes.add('advanced');
    return modes;
  }, [props.enableViewModeSwitch, props.enableSimpleView, props.enableReportView, props.enableAdvancedView]);

  const rawViewMode = (props.enableViewModeSwitch || props.enableSimpleView || props.enableReportView || props.enableAdvancedView)
    ? (urlViewMode ?? storedViewMode ?? 'table')
    : 'table';
  const currentViewMode: ListViewLayoutMode = allowedViewModes.has(rawViewMode) ? rawViewMode : 'table';

  // Row density & text size (persisted in preferences store)
  const tableDensity = usePreferencesStore((s) => s.tableDensity ?? 'comfortable');
  const setTableDensity = usePreferencesStore((s) => s.setTableDensity);
  const tableTextSize = usePreferencesStore((s) => s.tableTextSize ?? 'medium');
  const setTableTextSize = usePreferencesStore((s) => s.setTableTextSize);

  // Report configuration state (from store + URL)
  const { reportConfig, setReportConfig } = useShellReportConfig({ doctype, searchParams, setSearchParams });

  // Report mode detection
  const isReportMode = currentViewMode === 'report' && props.enableReportView;

  // Advanced table URL state — shares the shell's searchParams to avoid a 3rd useSearchParams subscription
  const advancedState = useAdvancedTableState({ searchParams, setSearchParams, defaultSort: defaultSorting });
  const clearAdvancedUrlParams = advancedState.clearUrlParams;

  const handleViewModeChange = useCallback((mode: ListViewLayoutMode) => {
    // Clean up advanced params when leaving advanced mode
    if (currentViewMode === 'advanced' && mode !== 'advanced') {
      clearAdvancedUrlParams();
    }
    // Update URL
    const newParams = new URLSearchParams(searchParams);
    if (mode === 'table') {
      newParams.delete('view');
    } else {
      newParams.set('view', mode);
    }
    setSearchParams(newParams, { replace: true });

    // Update localStorage
    usePreferencesStore.getState().setDocTypeViewMode(doctype, mode);
  }, [doctype, searchParams, setSearchParams, currentViewMode, clearAdvancedUrlParams]);

  // Use scoped filter store - select the specific scope's filters directly
  const filters = useFilterStore(
    useCallback(
      (state) => state.scopedFilters[filterStoreKey] ?? EMPTY_FILTERS,
      [filterStoreKey]
    )
  );

  // Use getState() for actions to avoid creating a second subscription
  const setFilters = useCallback(
    (newFilters: FilterDescriptor[]) => {
      useFilterStore.getState().setFiltersForKey(filterStoreKey, newFilters);
    },
    [filterStoreKey]
  );

  // Pagination state
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  });

  // Expansion state (for row hierarchy - variants, nested data, etc.)
  const [internalExpanded, setInternalExpanded] = useState<ExpandedState>({});

  // Sorting state - initialize with default
  const [sorting, setSorting] = useState<SortingState>(defaultSorting);

  // Reset sorting when defaultSorting changes (e.g. navigating between doctypes
  // reuses this component — useState initializer only runs on mount)
  useEffect(() => {
    setSorting(defaultSorting);
  }, [defaultSorting]);

  // Row selection state
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  // Filter modal states
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [isAdvancedFilterModalOpen, setIsAdvancedFilterModalOpen] = useState(false);

  // Focus mode — set by AdvancedTableView when entering edit mode
  const [isFocusMode, setIsFocusMode] = useState(false);

  // List settings - support both static and meta-driven modes
  const listSettingsKey = props.listSettingsConfig?.key || filterStoreKey;

  // IMPORTANT: Hooks must never be called conditionally.
  const _listSettings = useMetaDrivenListSettings({
    config: props.listSettingsConfig
      ? {
          ...props.listSettingsConfig,
          key: listSettingsKey,
        }
      : null,
    columnIds: props.listSettingsConfig?.meta?.columnIds,
  });

  const listSettings = props.listSettingsConfig ? _listSettings : null;

  const [isColumnSidebarOpen, setIsColumnSidebarOpen] = useState(false);

  // Normalize system columns config (single source of defaults)
  const systemConfig: NormalizedSystemConfig | null = useMemo(() => {
    if (!props.systemColumnsConfig?.enabled) return null;

    return {
      enabled: true,
      showAssignedTo: props.systemColumnsConfig.showAssignedTo ?? true,
      showCreated: props.systemColumnsConfig.showCreated ?? true,
      showComments: props.systemColumnsConfig.showComments ?? true,
      showLikes: props.systemColumnsConfig.showLikes ?? true,
      assignedToField: props.systemColumnsConfig.assignedToField ?? "_assign",
      commentCountField: props.systemColumnsConfig.commentCountField ?? "_comment_count",
      likedByField: props.systemColumnsConfig.likedByField ?? "_liked_by",
      createdField: props.systemColumnsConfig.createdField ?? "creation",
    };
  }, [props.systemColumnsConfig]);

  // Combined search state (debounced at page/UI layer)
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  // Convert sorting state to Frappe order_by format
  const orderBy = useMemo(() => {
    if (sorting.length === 0) {
      return undefined;
    }
    const sort = sorting[0];
    const direction = sort.desc ? "desc" : "asc";
    return `${sort.id} ${direction}`;
  }, [sorting]);

  // Reset pagination based on FilterDescriptor[] (store state), not mapped filters
  useEffect(() => {
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, [debouncedSearchQuery, filters, sorting]);

  const thumbnailToggleFieldname = props.thumbnailConfig?.toggleFieldname ?? "__thumbnail";
  const rawVisibleFieldOrder = listSettings?.visibleFieldOrder ?? [];

  // Never send synthetic UI toggles to the backend fields parameter.
  const visibleFieldOrderForBackend = useMemo(() => {
    return rawVisibleFieldOrder.filter(
      (f) => typeof f === "string" && !f.startsWith("__")
    );
  }, [rawVisibleFieldOrder]);

  const thumbnailEnabled =
    !!props.thumbnailConfig && rawVisibleFieldOrder.includes(thumbnailToggleFieldname);

  const needsImageFields = thumbnailEnabled || currentViewMode === 'gallery';

  const detectedImageFieldnames = useMemo(() => {
    if (!needsImageFields) return [];

    const explicit = props.thumbnailConfig?.imageFieldnames;
    if (explicit?.length) {
      // Developer-specified image fieldnames are trusted — don't filter against
      // list settings available fields, which excludes hidden DocType fields
      // (e.g. Item.image has hidden=1 but is still a valid fetchable field).
      // Non-existent fields are safely ignored by the backend (frappe.get_list
      // silently drops unknown fields via sanitize_fields).
      return explicit;
    }

    const candidates = (listSettings?.availableFields ?? [])
      .filter(
        (f: ListSettingsFieldConfig) =>
          f.fieldtype === "Attach Image" || f.fieldtype === "Image",
      )
      .map((f) => f.fieldname)
      .filter(Boolean);

    const preferred = ["primary_image", "image", "photo", "logo", "avatar", "user_image"];
    candidates.sort((a, b) => {
      const ai = preferred.indexOf(a);
      const bi = preferred.indexOf(b);
      return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
    });

    return candidates;
  }, [needsImageFields, props.thumbnailConfig?.imageFieldnames, listSettings?.availableFields]);

  const thumbnailLabelFieldnames = useMemo(() => {
    if (!thumbnailEnabled) return [];

    const explicit = props.thumbnailConfig?.labelFieldnames;
    if (explicit?.length) {
      // Filter against available fields so we never request fields that
      // don't exist on this DocType (e.g. "title" on Item Group).
      const available = new Set(
        (listSettings?.availableFields ?? []).map((f) => f.fieldname),
      );
      if (available.size > 0) {
        const filtered = explicit.filter((f) => f === 'name' || available.has(f));
        if (filtered.length > 0) return filtered;
      }
      return explicit;
    }

    const required = props.listSettingsConfig?.meta?.requiredFieldnames;
    if (required?.length) return required;

    return ["name"];
  }, [thumbnailEnabled, props.thumbnailConfig?.labelFieldnames, props.listSettingsConfig?.meta?.requiredFieldnames, listSettings?.availableFields]);

  // Gallery field getters (reuse existing image/label field detection)
  const {
    getGalleryImagePath,
    galleryTitleFieldnames,
    getGalleryTitleText,
    gallerySubtitleFieldnames,
    getGallerySubtitleText,
  } = useShellGalleryFields<T>({
    galleryConfig: props.galleryConfig,
    availableFields: listSettings?.availableFields,
    detectedImageFieldnames,
    thumbnailLabelFieldnames,
  });

  // Compute effective fields from visibleFieldOrder + technical fields + system fields
  const effectiveFields = useMemo(() => {
    if (!listSettings?.visibleFieldOrder) {
      if (!systemConfig) return undefined;

      const technical: string[] = ["name"];
      if (systemConfig.showAssignedTo) {
        technical.push(systemConfig.assignedToField);
      }
      if (
        systemConfig.showComments &&
        systemConfig.commentCountField !== "_comment_count"
      ) {
        technical.push(systemConfig.commentCountField);
      }
      if (systemConfig.showLikes) {
        technical.push(systemConfig.likedByField);
      }
      if (systemConfig.showCreated) {
        technical.push(systemConfig.createdField);
      }
      return technical;
    }

    const visible = visibleFieldOrderForBackend;
    const technical: string[] = [];

    technical.push("name");

    if (sorting.length > 0) {
      const sortField = sorting[0].id;
      if (sortField && !visible.includes(sortField) && !technical.includes(sortField)) {
        technical.push(sortField);
      }
    }

    if (props.listSettingsConfig?.meta?.requiredFieldnames) {
      props.listSettingsConfig.meta.requiredFieldnames.forEach((fname) => {
        if (!visible.includes(fname) && !technical.includes(fname)) {
          technical.push(fname);
        }
      });
    }

    if (needsImageFields) {
      for (const imgField of detectedImageFieldnames) {
        if (imgField && !visible.includes(imgField) && !technical.includes(imgField)) {
          technical.push(imgField);
        }
      }
      for (const lf of thumbnailLabelFieldnames) {
        if (lf && !visible.includes(lf) && !technical.includes(lf)) {
          technical.push(lf);
        }
      }
    }

    if (currentViewMode === 'gallery') {
      for (const f of [...galleryTitleFieldnames, ...gallerySubtitleFieldnames]) {
        if (f && !visible.includes(f) && !technical.includes(f)) {
          technical.push(f);
        }
      }
    }

    if (systemConfig) {
      if (systemConfig.showAssignedTo && !technical.includes(systemConfig.assignedToField)) {
        technical.push(systemConfig.assignedToField);
      }
      if (
        systemConfig.showComments &&
        systemConfig.commentCountField !== "_comment_count" &&
        !technical.includes(systemConfig.commentCountField)
      ) {
        technical.push(systemConfig.commentCountField);
      }
      if (systemConfig.showLikes && !technical.includes(systemConfig.likedByField)) {
        technical.push(systemConfig.likedByField);
      }
      if (systemConfig.showCreated && !technical.includes(systemConfig.createdField)) {
        technical.push(systemConfig.createdField);
      }
    }

    const effectiveFields = Array.from(new Set([...visible, ...technical]));
    return effectiveFields;
  }, [
    listSettings?.visibleFieldOrder,
    visibleFieldOrderForBackend,
    sorting,
    props.listSettingsConfig?.meta?.requiredFieldnames,
    systemConfig,
    thumbnailEnabled,
    needsImageFields,
    detectedImageFieldnames,
    thumbnailLabelFieldnames,
    currentViewMode,
    galleryTitleFieldnames,
    gallerySubtitleFieldnames,
  ]);

  // When in advanced mode, use nuqs-based pagination/sorting instead of shell state
  const effectivePagination = currentViewMode === 'advanced' ? advancedState.pagination : pagination;
  const effectiveOrderBy = currentViewMode === 'advanced' ? advancedState.orderBy : orderBy;

  // Use the provided hook for table/gallery modes
  const effectiveFilters = currentViewMode === 'advanced' ? advancedState.filterDescriptors : filters;
  const listResult = useListHook({
    filterDescriptors: effectiveFilters,
    searchQuery: debouncedSearchQuery,
    pageIndex: effectivePagination.pageIndex,
    pageSize: effectivePagination.pageSize,
    orderBy: effectiveOrderBy,
    fields: effectiveFields,
  });

  // Use report data hook for report mode
  const reportFields = reportConfig.visibleColumns.length > 0
    ? reportConfig.visibleColumns
    : undefined;

  const reportResult = useReportData({
    doctype,
    fields: reportFields,
    groupBy: reportConfig.groupByField,
    aggregations: reportConfig.aggregations,
    filterDescriptors: filters,
    orderBy,
    enabled: isReportMode,
  });

  // Extract items from paginated response (for table/gallery modes)
  const listData = listResult.data;
  const items = listData?.data || [];
  const total = listData?.total ?? 0;

  // Unified loading and error states
  const isLoading = isReportMode
    ? reportResult.isLoading
    : listResult.isLoading;
  const error = isReportMode ? reportResult.error : listResult.error;

  // Compute initial expanded state if defaultExpanded is provided
  useEffect(() => {
    if (!defaultExpanded || items.length === 0) return;

    const initialExpanded: ExpandedState = {};
    items.forEach((item) => {
      const rowId = getRowId(item);
      if (defaultExpanded(item)) {
        initialExpanded[rowId] = true;
      }
    });

    if (Object.keys(initialExpanded).length === 0) return;

    if (props.expanded !== undefined && props.onExpandedChange) {
      const current = asExpandedRecord(props.expanded);
      const merged: Record<string, boolean> = { ...current };

      let changed = false;
      for (const key of Object.keys(initialExpanded)) {
        if (merged[key] === undefined) {
          merged[key] = true;
          changed = true;
        }
      }

      if (changed) {
        props.onExpandedChange(merged as ExpandedState);
      }
    } else {
      if (Object.keys(internalExpanded).length === 0) {
        setInternalExpanded(initialExpanded);
      }
    }
  }, [items, defaultExpanded, getRowId, props.expanded, props.onExpandedChange, internalExpanded, setInternalExpanded]);

  // Extract all assignee IDs from current page for batched avatar fetching
  const allAssigneeIds = useMemo(() => {
    if (!systemConfig?.showAssignedTo) return [];

    const assigneeSet = new Set<string>();
    const assignedToField = systemConfig.assignedToField;

    for (const item of items) {
      const raw = item as unknown as Record<string, unknown>;
      const value = raw[assignedToField] as string | string[] | null | undefined;
      const users = parseLikedBy(value);

      users.forEach((userId) => {
        if (userId?.trim()) {
          assigneeSet.add(userId.trim());
        }
      });
    }

    return Array.from(assigneeSet);
  }, [items, systemConfig?.showAssignedTo, systemConfig?.assignedToField]);

  // Fetch user avatars in batch (one API call per page)
  const { avatarMap } = useUserAvatars(allAssigneeIds);

  // Use controlled expanded state if provided, otherwise use internal state
  const effectiveExpanded = props.expanded !== undefined ? props.expanded : internalExpanded;
  const effectiveOnExpandedChange = props.onExpandedChange || setInternalExpanded;

  // Column building + useReactTable (extracted to useShellColumns hook)
  const { table } = useShellColumns<T>({
    columns,
    items,
    visibleFieldOrderForBackend,
    listSettings,
    thumbnailEnabled,
    thumbnailConfig: props.thumbnailConfig,
    detectedImageFieldnames,
    thumbnailLabelFieldnames,
    onRowClick,
    getSubRows,
    systemConfig,
    doctype,
    avatarMap,
    prependColumn: props.prependColumn,
    prependColumns: props.prependColumns,
    pagination,
    sorting,
    rowSelection,
    total,
    getRowId,
    effectiveExpanded,
    effectiveOnExpandedChange,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    onRowSelectionChange: setRowSelection,
    setIsColumnSidebarOpen,
  });

  // Inline editing for standard table (Plan 161, Epic 02)
  const isInlineEditMode = props.isInlineEditMode ?? false;
  const { editableFields: inlineEditableFields } = useFieldEditability(
    props.inlineEditable ? doctype : ''
  );
  const inlineEditingContext = useInlineTableEditing({
    doctype,
    editableFields: props.inlineEditable ? inlineEditableFields : new Set(),
    table,
    getRowId,
  });

  // Page-aware AI (plan 1968): publish what this list shows + which rows are ticked
  const isAdvancedView = currentViewMode === 'advanced';
  usePublishListContext({
    doctype,
    listKey: filterStoreKey,
    filters: effectiveFilters,
    search: debouncedSearchQuery,
    sorting: isAdvancedView ? advancedState.sorting : sorting,
    pagination: effectivePagination,
    total: listData ? total : undefined,
    viewMode: currentViewMode,
    rowSelection,
    openDetail: searchParams.get('detail'),
  });
  const screenRef = useListScreenSection({ title: title ?? doctype, total: listData ? total : undefined, version: items });

  // Bulk archive/restore handlers for SelectionFloatingBar
  const { handleBulkArchive, handleBulkRestore } = useShellBulkActions({
    doctype,
    rowSelection,
    setRowSelection,
    refetch: listResult.refetch,
  });

  if (isLoading && currentViewMode !== 'advanced') {
    return (
      <PageContainer fullWidth={props.fullWidth}>
        <div className="flex items-center justify-center h-64">
          <div className="text-muted-foreground">
            {translations.loadingItems || "Loading..."}
          </div>
        </div>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer fullWidth={props.fullWidth}>
        <div className="flex items-center justify-center h-64">
          <div className="text-destructive">
            {translations.errorLoadingItems || "Error loading items"}:{" "}
            {error instanceof Error ? error.message : translations.unknownError || "Unknown error"}
          </div>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer className={className || "pt-12"} fullWidth={props.fullWidth || isFocusMode} noPadding={props.noPadding}>
      <div className={props.compactMode ? "space-y-0" : "space-y-6"}>
        {/* ── Toolbar (compact or standard) ── */}
        <ListToolbar<T>
          compactMode={props.compactMode}
          isFocusMode={isFocusMode}
          isReportMode={!!isReportMode}
          isLoading={isLoading}
          noPadding={props.noPadding}
          currentViewMode={currentViewMode}
          onViewModeChange={handleViewModeChange}
          enableSimpleView={props.enableSimpleView}
          enableViewModeSwitch={props.enableViewModeSwitch}
          enableReportView={props.enableReportView}
          enableAdvancedView={props.enableAdvancedView}
          breadcrumbs={props.breadcrumbs}
          doctype={doctype}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          searchPlaceholder={searchPlaceholder}
          searchConfig={props.searchConfig}
          onSearchSelect={props.onSearchSelect}
          filters={filters}
          onFiltersChange={setFilters}
          onOpenFilterModal={() => setIsFilterModalOpen(true)}
          onOpenAdvancedFilterModal={() => setIsAdvancedFilterModalOpen(true)}
          table={table}
          tableDensity={tableDensity}
          setTableDensity={setTableDensity}
          tableTextSize={tableTextSize}
          setTableTextSize={setTableTextSize}
          listSettings={listSettings}
          onOpenColumnSidebar={() => setIsColumnSidebarOpen(true)}
          reportConfig={reportConfig}
          onReportConfigChange={setReportConfig}
          header={header}
          headerBelow={headerBelow}
          headerActions={props.headerActions}
          filterStoreKey={props.filterStoreKey}
          title={title}
          description={description}
          newButtonLabel={newButtonLabel}
          onNewClick={onNewClick}
          translations={translations}
        />

        {/* ── View content: Simple / Advanced / Report / Table / Gallery ── */}
        <div ref={screenRef} className={cn("flex flex-col gap-2.5 overflow-hidden", props.compactMode && props.noPadding && "px-0")}>
          {currentViewMode === 'simple' && props.renderSimpleView ? (
            props.renderSimpleView({ items, isLoading, total, refetch: () => listResult.refetch?.() })
          ) : currentViewMode === 'advanced' ? (
            <AdvancedTableView
              items={items}
              total={total}
              isLoading={isLoading}
              columns={props.advancedColumns ?? []}
              onRowClick={onRowClick}
              getRowId={getRowId}
              pageSizeOptions={props.pageSizeOptions}
              advancedState={advancedState}
              doctype={doctype}
              onMutationSuccess={() => listResult.refetch?.()}
              labels={props.advancedTableLabels}
              columnOverrides={props.advancedColumnOverrides}
              selectOptions={props.advancedFilterOptions}
              editableFields={props.advancedEditableFields}
              dataGridLabels={props.advancedDataGridLabels}
              onFocusModeChange={setIsFocusMode}
              showArchived={props.showArchived}
            />
          ) : currentViewMode === 'report' && isReportMode ? (
            <RevoGridReport
              doctype={doctype}
              data={reportResult.data}
              visibleColumns={reportConfig.visibleColumns}
              aggregations={reportConfig.aggregations}
              groupByField={reportConfig.groupByField}
              onRowClick={(row) => onRowClick?.(row as T)}
              isLoading={isLoading}
            />
          ) : currentViewMode === 'table' ? (
            <ListTableBody<T>
              table={table}
              tableDensity={tableDensity}
              tableTextSize={tableTextSize}
              onRowClick={onRowClick}
              tableContainerClassName={props.tableContainerClassName}
              translations={translations}
              renderSubRow={renderSubRow}
              activeSubRowId={activeSubRowId}
              getRowId={getRowId}
              inlineEditing={props.inlineEditable && isInlineEditMode ? inlineEditingContext : undefined}
            />
          ) : (
            <GalleryGridLayout<T>
              items={items}
              getRowId={getRowId}
              getImagePath={getGalleryImagePath}
              getTitleText={getGalleryTitleText}
              getSubtitleText={getGallerySubtitleText}
              onRowClick={onRowClick}
              isLoading={isLoading}
              hasImageFields={detectedImageFieldnames.length > 0}
              sizePx={props.galleryConfig?.sizePx ?? 160}
              className="px-1"
              getSubRows={getSubRows}
            />
          )}
          {/* Pagination - show for simple/table/gallery modes (advanced/report have their own) */}
          {!isReportMode && currentViewMode !== 'advanced' && (
            <div className="flex flex-col gap-2.5 p-4">
              <DataTablePagination table={table} pageSizeOptions={props.pageSizeOptions} />
            </div>
          )}
        </div>
      </div>
      <ListFilterModals
        isFilterModalOpen={isFilterModalOpen}
        onFilterModalOpenChange={setIsFilterModalOpen}
        isAdvancedFilterModalOpen={isAdvancedFilterModalOpen}
        onAdvancedFilterModalOpenChange={setIsAdvancedFilterModalOpen}
        doctype={doctype}
        filters={filters}
        onFiltersChange={setFilters}
        translations={translations}
        isColumnSidebarOpen={isColumnSidebarOpen}
        onColumnSidebarClose={() => setIsColumnSidebarOpen(false)}
        listSettings={listSettings}
        systemColumnToggles={props.systemColumnToggles}
        onSystemColumnToggle={props.onSystemColumnToggle}
      />
      {/* Floating selection bar for bulk editing and archive/restore */}
      {Object.keys(rowSelection).length > 0 && currentViewMode === 'table' && (
        <SelectionFloatingBar
          selectedCount={Object.keys(rowSelection).length}
          inlineEditing={props.inlineEditable && isInlineEditMode ? inlineEditingContext : undefined}
          table={table}
          doctype={doctype}
          getRowId={getRowId}
          onClearSelection={() => setRowSelection({})}
          onArchive={handleBulkArchive}
          onRestore={handleBulkRestore}
          showRestore={props.showArchived}
        />
      )}
    </PageContainer>
  );
}
